import { observeMatchingElements } from './observeMatchingElements';
const OVERLAY_SELECTOR = '.modal-overlay [role="dialog"]';

type ViewportObservers = {
  controls?: ResizeObserver | null;
  viewport?: ResizeObserver | null;
  mutation?: MutationObserver | null;
};

const enhancedDialogs = new WeakSet<HTMLElement>();
const bodyObservers = new WeakMap<HTMLElement, MutationObserver>();
const viewportRegistry = new WeakMap<HTMLElement, ViewportObservers>();
const activeViewports = new Set<HTMLElement>();

let installed = false;

function setClass(element: HTMLElement, name: string, enabled = true): void {
  if (element.classList.contains(name) !== enabled) element.classList.toggle(name, enabled);
}

export function installEditModalEnhancer(): void {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  observeMatchingElements(OVERLAY_SELECTOR, enhanceDialog);

  window.addEventListener('resize', () => {
    activeViewports.forEach(viewport => {
      if (!viewport.isConnected) {
        activeViewports.delete(viewport);
        return;
      }
      updateStickyOffset(viewport);
    });
  });

}

function enhanceDialog(dialog: HTMLElement): void {
  if (!dialog.isConnected) return;

  const header = findHeader(dialog);
  const title = header?.querySelector<HTMLElement>('h1, h2, h3, .modal__title')
    || dialog.querySelector<HTMLElement>('h1, h2, h3, .modal__title');
  const titleText = (title?.textContent || '').trim();
  if (!/^edit\b/i.test(titleText)) return;

  setClass(dialog, 'edit-modal--sticky');

  if (header) {
    setClass(header, 'em-header');
  }

  const body = findBody(dialog);
  if (!body) return;
  setClass(body, 'em-body');

  const footer = ensureFooter(dialog, body);
  if (footer) {
    setClass(footer, 'em-footer');
  }

  moveActionButtons(body, footer);

  applyTableEnhancements(body);

  if (!enhancedDialogs.has(dialog)) {
    enhancedDialogs.add(dialog);
    installBodyObserver(body);
  }
}

function findHeader(root: HTMLElement): HTMLElement | null {
  const selectors = [
    ':scope > .em-header',
    ':scope > .modal__header',
    ':scope > .modal-header',
    ':scope > header',
    '.modal__header',
    '.modal-header',
  ];
  for (const selector of selectors) {
    try {
      const node = root.querySelector<HTMLElement>(selector);
      if (node) return node;
    } catch {
      // ignore selector errors
    }
  }
  return null;
}

function findBody(root: HTMLElement): HTMLElement | null {
  const selectors = [
    ':scope > .em-body',
    ':scope > .modal__body-content',
    ':scope > .modal__body',
    ':scope > .modal-body',
    '.modal__body-content',
    '.modal__body',
    '.modal-body',
  ];
  for (const selector of selectors) {
    try {
      const node = root.querySelector<HTMLElement>(selector);
      if (node) return node;
    } catch {
      // ignore selector errors
    }
  }
  return null;
}

function ensureFooter(dialog: HTMLElement, body: HTMLElement): HTMLElement | null {
  const selectors = [
    ':scope > .em-footer',
    ':scope > .modal__footer',
    ':scope > .modal-footer',
    ':scope > .modal-actions',
    '.em-footer',
    '.modal__footer',
    '.modal-footer',
    '.modal-actions',
  ];
  for (const selector of selectors) {
    try {
      const node = dialog.querySelector<HTMLElement>(selector);
      if (node && dialog.contains(node)) {
        return node;
      }
    } catch {
      // ignore selector errors
    }
  }

  const footer = document.createElement('footer');
  footer.className = 'em-footer';
  dialog.appendChild(footer);
  return footer;
}

function moveActionButtons(body: HTMLElement, footer: HTMLElement | null): void {
  if (!footer) return;

  const buttons = Array.from(body.querySelectorAll<HTMLButtonElement>('button'));
  const saveBtn = buttons.find(btn => /save/i.test(btn.textContent || ''));
  const cancelBtn = buttons.find(btn => /cancel/i.test(btn.textContent || ''));

  [cancelBtn, saveBtn].forEach(btn => {
    if (!btn) return;
    if (!footer.contains(btn)) {
      const parentForm = btn.closest('form');
      if (parentForm && !parentForm.contains(footer)) {
        // Keep buttons within their form to preserve submit behaviour.
        parentForm.appendChild(footer);
      }
      footer.appendChild(btn);
    }
  });
}

function installBodyObserver(body: HTMLElement): void {
  if (bodyObservers.has(body)) return;
  let frame: number | undefined;
  const observer = new MutationObserver(records => {
    // Ignore our own idempotent class/style writes and coalesce real changes.
    const changed = records.some(record => record.type !== 'attributes'
      || record.oldValue !== (record.target as Element).getAttribute(record.attributeName!));
    if (!changed || frame !== undefined) return;
    frame = requestAnimationFrame(() => { frame = undefined; applyTableEnhancements(body); });
  });
  observer.observe(body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
    attributeOldValue: true,
  });
  bodyObservers.set(body, observer);
}

function applyTableEnhancements(body: HTMLElement): void {
  if (!body.isConnected) return;

  const tables = Array.from(body.querySelectorAll<HTMLTableElement>('table'))
    .filter(table => isElementVisible(table));

  const processedViewports = new Set<HTMLElement>();

  tables.forEach(table => {
    const viewport = ensureTableViewport(table);
    if (!viewport) return;
    processedViewports.add(viewport);
    setupViewport(viewport);
  });

  if (processedViewports.size === 0) {
    setClass(body, 'em-body--scroll');
  } else {
    setClass(body, 'em-body--scroll', false);
  }
}

function isElementVisible(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  if (element.offsetParent === null && element !== document.body) {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
}

function ensureTableViewport(table: HTMLTableElement): HTMLElement | null {
  const existingViewport = table.closest<HTMLElement>('.em-table-viewport');
  if (existingViewport) {
    setClass(table, 'em-data-table');
    ensureControls(existingViewport, table);
    return existingViewport;
  }

  const cardableRoot = table.closest<HTMLElement>('.cardable-table');
  if (cardableRoot) {
    return ensureCardableViewport(cardableRoot, table);
  }

  const parent = table.parentElement;
  if (!parent) return null;

  const viewport = document.createElement('div');
  viewport.className = 'em-table-viewport';
  parent.insertBefore(viewport, table);
  viewport.appendChild(table);
  setClass(table, 'em-data-table');

  const controls = findNearbyControls(table);
  if (controls) {
    setClass(controls, 'em-table-controls');
    viewport.insertBefore(controls, viewport.firstChild);
  }

  return viewport;
}

function ensureCardableViewport(cardableRoot: HTMLElement, table: HTMLTableElement): HTMLElement | null {
  let viewport = cardableRoot.querySelector<HTMLElement>(':scope > .em-table-viewport');
  if (!viewport) {
    viewport = document.createElement('div');
    viewport.className = 'em-table-viewport';
    const controls = cardableRoot.querySelector<HTMLElement>(':scope > .cardable-table__controls');
    const scroll = cardableRoot.querySelector<HTMLElement>(':scope > .cardable-table__scroll');
    const anchor = controls || scroll || null;
    if (anchor) {
      cardableRoot.insertBefore(viewport, anchor);
    } else {
      cardableRoot.appendChild(viewport);
    }
    if (controls && controls.parentElement === cardableRoot) {
      viewport.appendChild(controls);
    }
    if (scroll && scroll.parentElement === cardableRoot) {
      viewport.appendChild(scroll);
    }
  }

  const controls = viewport.querySelector<HTMLElement>('.cardable-table__controls');
  if (controls) {
    setClass(controls, 'em-table-controls');
  }

  const scroll = viewport.querySelector<HTMLElement>('.cardable-table__scroll');
  if (scroll) {
    setClass(scroll, 'em-table-scroll');
  }

  setClass(table, 'em-data-table');
  return viewport;
}

function findNearbyControls(table: HTMLTableElement): HTMLElement | null {
  const selector = '.em-table-controls, .table-controls, .list-grid-toggle, [data-view-toggle], .table-filters';
  let current: HTMLElement | null = table.previousElementSibling as HTMLElement | null;
  while (current) {
    if (current.matches(selector)) return current;
    if (/^H[1-6]$/.test(current.tagName)) break;
    current = current.previousElementSibling as HTMLElement | null;
  }

  const parent = table.parentElement;
  if (parent) {
    const candidate = parent.querySelector<HTMLElement>(selector);
    if (candidate && candidate.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return candidate;
    }
  }
  return null;
}

function ensureControls(viewport: HTMLElement, table: HTMLTableElement): void {
  const controls = viewport.querySelector<HTMLElement>('.em-table-controls');
  if (controls) return;
  const candidate = findNearbyControls(table);
  if (candidate) {
    setClass(candidate, 'em-table-controls');
    if (candidate.parentElement !== viewport) {
      viewport.insertBefore(candidate, viewport.firstChild);
    }
  }
}

function setupViewport(viewport: HTMLElement): void {
  setClass(viewport, 'em-table-viewport-active');
  activeViewports.add(viewport);
  updateStickyOffset(viewport);

  if (viewportRegistry.has(viewport)) return;

  const controls = viewport.querySelector<HTMLElement>('.em-table-controls');

  const observers: ViewportObservers = {};

  if (typeof ResizeObserver !== 'undefined') {
    observers.viewport = new ResizeObserver(() => updateStickyOffset(viewport));
    observers.viewport.observe(viewport);
    if (controls) {
      observers.controls = new ResizeObserver(() => updateStickyOffset(viewport));
      observers.controls.observe(controls);
    }
  }

  observers.mutation = new MutationObserver(() => updateStickyOffset(viewport));
  observers.mutation.observe(viewport, { childList: true, subtree: true });

  viewportRegistry.set(viewport, observers);
}

function updateStickyOffset(viewport: HTMLElement): void {
  if (!viewport.isConnected) {
    activeViewports.delete(viewport);
    const observers = viewportRegistry.get(viewport);
    if (observers) {
      observers.viewport?.disconnect();
      observers.controls?.disconnect();
      observers.mutation?.disconnect();
      viewportRegistry.delete(viewport);
    }
    return;
  }

  const controls = viewport.querySelector<HTMLElement>('.em-table-controls');
  const height = controls ? Math.round(controls.getBoundingClientRect().height) : 0;
  const offset = `${height}px`;
  if (viewport.style.getPropertyValue('--em-sticky-offset') !== offset) {
    viewport.style.setProperty('--em-sticky-offset', offset);
  }
}
